import React, {useState} from "react";
import {observer} from "mobx-react";
import GeneralDialog from "./GeneralDialog";
import {TextareaAutosize, Tooltip} from "@mui/material";
import {useStores} from "../../Hooks";
import LimitedTextField from "../Shared/LimitedTextField";
import {ObservableMap} from "mobx";
import Checkbox from "@mui/material/Checkbox";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {DATA_INTERVAL} from "../../MobxStores/DataStore";
import sharedUtils from "../../serverClientShared/sharedUtils";
import ConditionalComponent from "../Shared/ConditionalComponent";
import {procedureTypeEnum} from "../../serverClientShared/sharedEnums";
import Utility from "../../Utility";

const PublishVersionModal = observer(() => {
    const {modalStore, generalStore, procedureStore, dataStore, projectStore} = useStores();

    const [currentVersionStr, setCurrentVersionStr] = useState('');
    const [suggestedNextVersion, setSuggestedNextVersion] = useState('');
    const [nextVersionStr, setNextVersionStr] = useState('');
    const [releaseNotes, setReleaseNotes] = useState('');
    const [usedVersions, setUsedVersions] = useState([]);
    const [payPerClickSelected, setPayPerClickSelected] = useState(false);
    const [sitePrepNeedsApproval, setSitePrepNeedsApproval] = useState(false);

    const isSitePrep = procedureStore.currentProcedure?.ProcedureType === procedureTypeEnum.SITE_PREP;

    function isVersionValid() {
        const onlyNumbersAndDots = /^[.\d]*$/.test(nextVersionStr);
        const versionParts = nextVersionStr.split('.');
        const partsInvalid = versionParts.filter(versionPart => {
            return !/^\d+$/.test(versionPart) || versionPart.length > 2;
        }).length;
        return onlyNumbersAndDots && !partsInvalid && !usedVersions.includes(nextVersionStr);
    }

    async function checkNextVersionStr() {
        if (!isVersionValid) {
            setNextVersionStr(suggestedNextVersion);
            modalStore.showErrorMessage(generalStore.translate('invalidVersionNumSentence'));
        }
    }

    function handleAllowPayPerClick() {
        if (!!generalStore.checkWorkspaceAdmin) {
            if (!payPerClickSelected) {
                modalStore.modalData.payPerClick.isTemp = true;
                modalStore.modalData.payPerClick.ProcedureName = procedureStore.currentProcedure.ProcedureName;
                modalStore.modalData.payPerClick.ProjectName = projectStore.currentProject?.ProjectName;
                modalStore.modalData.afterSave = () => setPayPerClickSelected(true);
                modalStore.isPayPerClickSharingOpen = true;
            } else {
                setPayPerClickSelected(false);
            }
        } else {
            if (!payPerClickSelected) {
                modalStore.modalData.payPerClick.IsPending = true;
            } else {
                delete modalStore.modalData.payPerClick.IsPending;
            }
            setPayPerClickSelected(!payPerClickSelected);
        }
    }

    function renderBody() {

        return (
            <div className='body-container'>
                <div className='title-container'>
                    <ConditionalComponent if={modalStore.modalData.directFromNotification}>
                        <span><b>{procedureStore.currentProcedure.ProcedureName}</b></span>
                    </ConditionalComponent>
                    <ConditionalComponent if={!isSitePrep}>
                        <span>{generalStore.translate('YouAreAboutToPublishANewVersionOfTheItem')}</span>
                        <span>{generalStore.translate('aboutToPublishSentence2')}</span>
                    </ConditionalComponent>
                    <ConditionalComponent if={isSitePrep}>
                        <span>{generalStore.translate('youAreAboutToPublishA')} {generalStore.translate('newVersionOfYourSitePrep', true)}</span>
                        <ConditionalComponent if={sitePrepNeedsApproval}>
                            <span>({generalStore.translate('anApprovalRequestWillBeSentToTheWorkspaceAdmin', true)})</span>
                        </ConditionalComponent>
                        <ConditionalComponent if={!sitePrepNeedsApproval}>
                            <span>{generalStore.translate('aboutToPublishSentence2')}</span>
                        </ConditionalComponent>
                    </ConditionalComponent>
                </div>
                <div className='versions-container'>
                    <div className='version-container'>
                        {generalStore.translate('currentVersionNum')}: <span
                        className='current-version'>{currentVersionStr}</span>
                    </div>
                    <div className='version-container'>
                        {generalStore.translate('newVersionNum')}:
                        <LimitedTextField
                            className='new-version-input'
                            variant='outlined'
                            onChange={(e) => setNextVersionStr(e.target.value)}
                            value={nextVersionStr}
                            onBlur={checkNextVersionStr}
                            error={!isVersionValid()}
                            originalValue={nextVersionStr}
                            limit={500}
                            inputProps={{'data-testid': 'publish-version-input'}}
                        />
                    </div>
                </div>
                <div className='release-notes-container'>
                    <div className='release-notes-title'>
                        {generalStore.translate('changesInThisVersion')}
                    </div>
                    <div className='release-notes-input-container'>
                        <TextareaAutosize
                            className='release-notes-input'
                            variant='outlined'
                            onChange={(e) => setReleaseNotes(e.target.value)}
                            minRows={4}
                            value={releaseNotes}
                            data-testid="publish-release-notes"
                        />
                    </div>
                    <ConditionalComponent
                        if={procedureStore.currentProcedure?.ProcedureType !== procedureTypeEnum.SITE_PREP}>
                        <div className='pay-per-click-checkbox'>
                            <Checkbox
                                checked={payPerClickSelected}
                                onChange={handleAllowPayPerClick}
                                name="payPerClickCheckbox"
                                disabled={!!modalStore.modalData?.payPerClick?.NoChangeOnPublish}
                                data-testid="publish-ppc-checkbox"
                            />
                            <div className="checkbox-label">
                                <span>{generalStore.translate('allowPayPerClickSharing')}</span>
                                <Tooltip title={generalStore.translate('payPerClickSharingTooltip')}
                                         placement="right-end"
                                         arrow>
                                    <InfoOutlinedIcon/>
                                </Tooltip>
                            </div>
                        </div>
                    </ConditionalComponent>
                </div>
            </div>
        )

    }

    async function publish() {
        if (!isVersionValid()) {
            return;
        }

        if (sitePrepNeedsApproval) {
            procedureStore.sendSitePrepPublishRequest().then(() => modalStore.showSuccessMessage(generalStore.translate('publishApprovalRequestSent')));
            modalStore.isPublishVersionModal = false;
            Utility.sleep(100).then(() => modalStore.resetModalData());
            return;
        }

        try {
            const versionObj = {versionNumber: nextVersionStr, releaseNotes};
            const sendPayPerClick = payPerClickSelected && (!!modalStore.modalData?.payPerClick && !modalStore.modalData.payPerClick.NoChangeOnPublish);
            const selectedLanguage = procedureStore.currentProcedure.selectedLanguage;
            generalStore.isLoaderOpen = true;
            const procedure = await procedureStore.publishProcedure(versionObj, sendPayPerClick ? modalStore.modalData.payPerClick : null);
            if(!projectStore.currentProject.Default3DModel && procedure.ProcedureType === procedureTypeEnum.FUSION)
                projectStore.currentProject.Default3DModel = procedureStore.currentProcedure.ProcedureIdentifier;
            procedureStore.getProcedureVersions().then();
            procedure.selectedLanguage = selectedLanguage;
            if (sendPayPerClick && !generalStore.checkWorkspaceAdmin) {
                modalStore.showSuccessMessage(generalStore.translate('payPerClickPermissionRequestHasBeenSentToYourWorkspaceAdmin'))
            }
            modalStore.modalData.sharingId = procedure.ProcedureIdentifier;
            modalStore.modalData.onClose = async () => {
                procedure.IsPublished = true;
                procedure.CurrentVersion = nextVersionStr;

                const projectId = projectStore.currentProject?.ProjectIdentifier;
                const projectProceduresMap = dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();
                projectProceduresMap.set(procedure.ProcedureIdentifier, procedure);
                dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
                dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
                if (procedureStore.fetchedProceduresMap.size) {
                    procedureStore.fetchedProceduresMap.set(procedure.ProcedureIdentifier, procedure);
                } else {
                    procedureStore.currentProcedure = procedure;
                }
                if (modalStore.modalData.showPublish) {
                    modalStore.modalData.showPublish();
                }

            };
            modalStore.isProcedurePublishedModalOpen = true;
            delete modalStore.modalData.currentVersionStr;
            delete modalStore.modalData.payPerClick;
            setPayPerClickSelected(false);
            modalStore.isPublishVersionModal = false;
        } catch (e) {
            console.error(e);
            modalStore.showErrorMessage(generalStore.translate('cannotPublishVersion'));
        }
        generalStore.isLoaderOpen = false;
    }

    async function onEnter() {

        generalStore.isLoaderOpen = true;
        const [payPerClick,] = await Promise.all([
            procedureStore.getPayPerClickStatus(),
            procedureStore.getProcedureVersions(),
        ]);
        modalStore.modalData.payPerClick = payPerClick || {};
        const currentVersionStr = procedureStore.currentProcedure?.CurrentVersion || modalStore.modalData.currentVersionStr || '---';
        const procedureVersions = procedureStore.procedureVersions;
        const versionsArray = procedureVersions.map(procedureVersion => procedureVersion.Version);
        const suggestedNextVersion = sharedUtils.getNextVersion(versionsArray);
        setCurrentVersionStr(currentVersionStr);
        setSuggestedNextVersion(suggestedNextVersion);
        setNextVersionStr(suggestedNextVersion);
        setReleaseNotes('');
        setUsedVersions(versionsArray);
        setPayPerClickSelected(!!modalStore.modalData.payPerClick.ProcedureRef);
        setSitePrepNeedsApproval(isSitePrep && !procedureStore.currentProcedure.IsPublished && !sharedUtils.isUserAdmin(generalStore.workspace, generalStore.userDetails));
        generalStore.isLoaderOpen = false;
    }

    return (
        <GeneralDialog
            openControl='isPublishVersionModal'
            className='procedure-option-dialog publish-procedure-dialog'
            data-testid="publish-procedure-dialog"
            onEnter={onEnter}
            title={generalStore.translate(isSitePrep ? 'publishSitePrep' : 'publish')}
            body={renderBody()}
            isConfirmDisabled={!isVersionValid()}
            onConfirm={publish}
            confirmText={generalStore.translate(sitePrepNeedsApproval ? 'requestPublishApproval' : 'publish')}
        />
    );

});

export default PublishVersionModal;
